/**
 * Reload once when the page asks for a chunk the deploy replaced.
 *
 * The app is code-split and every chunk filename carries a content hash, so a
 * deploy renames them. A tab open across one is holding an `index.html` that
 * points at files the server no longer has: the shell keeps working and the
 * next route someone visits fails to load. Observed in production as
 *
 *     GET /assets/Campaigns-B5z9MBdI.js   404
 *
 * on a page that was otherwise fine. Nobody knows to press reload, because
 * nothing says anything — the screen simply stops going anywhere.
 *
 * Vite fires `vite:preloadError` when a dynamic import fails, in built output
 * as well as in dev, which is the one moment we know for certain the page is
 * older than the server.
 *
 * ONCE. If the reload does not fix it — an actually missing file, a broken
 * deploy, a proxy serving stale HTML — a second attempt fails the same way and
 * the third is an infinite loop pointed at whoever is unlucky enough to be
 * reading. The flag lives in sessionStorage so it clears with the tab, and is
 * cleared on the next successful navigation so a later deploy is handled too.
 */
const FLAG = 'fronted:reloaded-for-stale-build'

export function reloadOnStaleBuild(): void {
  if (typeof window === 'undefined') return

  window.addEventListener('vite:preloadError', (event) => {
    let already = false
    try {
      already = sessionStorage.getItem(FLAG) === '1'
      sessionStorage.setItem(FLAG, '1')
    } catch {
      // Private browsing, or storage disabled. Reloading once is still worth
      // trying; without the flag the guard is the browser's own patience.
    }
    if (already) return

    // Stop React from also surfacing it as a render error in the same tick.
    event.preventDefault()
    window.location.reload()
  })

  // The page loaded and ran, so whatever went wrong is behind us.
  window.addEventListener('load', () => {
    try {
      sessionStorage.removeItem(FLAG)
    } catch {
      /* nothing to clear */
    }
  })
}
